import request from '../utils/request';
import { ApiResponse } from '../types';

export interface KnowledgeQAItem {
  id: number;
  question: string;
  answer: string;
  images: string | null;
  videos: string | null;
  sort_order: number;
  create_time: string;
  update_time: string;
}

export interface KnowledgeQACreateParams {
  question: string;
  answer: string;
  images?: string | null;
  videos?: string | null;
  sort_order?: number;
}

export interface KnowledgeQAUpdateParams {
  question?: string;
  answer?: string;
  images?: string | null;
  videos?: string | null;
  sort_order?: number;
}

/** 知识库问答列表（管理员） */
export const getKnowledgeQAList = async (): Promise<ApiResponse<KnowledgeQAItem[]>> => {
  return request.get('/admin/knowledge-qa');
};

/** 知识库问答详情（管理员） */
export const getKnowledgeQAById = async (id: number): Promise<ApiResponse<KnowledgeQAItem>> => {
  return request.get(`/admin/knowledge-qa/${id}`);
};

/** 新增知识库问答（管理员） */
export const createKnowledgeQA = async (
  params: KnowledgeQACreateParams
): Promise<ApiResponse<KnowledgeQAItem>> => {
  return request.post('/admin/knowledge-qa', params);
};

/** 更新知识库问答（管理员） */
export const updateKnowledgeQA = async (
  id: number,
  params: KnowledgeQAUpdateParams
): Promise<ApiResponse<KnowledgeQAItem>> => {
  return request.put(`/admin/knowledge-qa/${id}`, params);
};

/** 删除知识库问答（管理员） */
export const deleteKnowledgeQA = async (id: number): Promise<ApiResponse<void>> => {
  return request.delete(`/admin/knowledge-qa/${id}`);
};

// ---------- 会员管理 ----------
export interface MemberItem {
  id: number;
  username: string;
  phone: string | null;
  email: string;
  avatar: string | null;
  role: string;
  status: number;
  create_time: string;
  update_time: string;
}

/** 会员列表（管理员） */
export const getMemberList = async (): Promise<ApiResponse<MemberItem[]>> => {
  return request.get('/admin/members');
};

/** 切换会员状态（管理员） */
export const toggleMemberStatus = async (id: number, status: number): Promise<ApiResponse<void>> => {
  return request.put(`/admin/members/${id}/status`, { status });
};

/** 删除会员（管理员） */
export const deleteMember = async (id: number): Promise<ApiResponse<void>> => {
  return request.delete(`/admin/members/${id}`);
};

// ---------- 文件上传 ----------
export interface UploadResult {
  url: string;
  filename: string;
  originalname: string;
  mimetype: string;
  size: number;
}

/** 上传文件（管理员） */
export const uploadFile = async (file: File): Promise<ApiResponse<UploadResult>> => {
  const formData = new FormData();
  formData.append('file', file);
  return request.post('/admin/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/** 上传多个文件（管理员） */
export const uploadFiles = async (files: File[]): Promise<ApiResponse<UploadResult[]>> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return request.post('/admin/upload-multiple', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ---------- 短信接口配置 ----------
export interface SmsConfigData {
  use_aliyun: boolean;
  access_key_id: string;
  access_key_secret: string;
  sign_name: string;
  template_code: string;
}

/** 获取短信接口配置（管理员） */
export const getSmsConfig = async (): Promise<ApiResponse<SmsConfigData>> => {
  return request.get('/admin/sms-config');
};

/** 更新短信接口配置（管理员） */
export const updateSmsConfig = async (params: SmsConfigData): Promise<ApiResponse<void>> => {
  return request.put('/admin/sms-config', params);
};

// ---------- 系统设置 ----------
export interface SystemSettingsData {
  id?: number;
  site_name: string;
  logo_url: string;
  ai_icon_url: string;
  phone_number: string;
}

/** 获取系统设置（管理员） */
export const getSystemSettings = async (): Promise<ApiResponse<SystemSettingsData>> => {
  return request.get('/admin/system-settings');
};

/** 更新系统设置（管理员） */
export const updateSystemSettings = async (params: Partial<SystemSettingsData>): Promise<ApiResponse<void>> => {
  return request.put('/admin/system-settings', params);
}

// ---------- 快捷链接 ----------
export interface QuickLinkItem {
  id: number;
  title: string;
  description: string;
  image_url: string;
  icon_svg: string | null;
  link_url: string;
  sort_order: number;
  is_visible: number;
  create_time: string;
  update_time: string;
}

/** 快捷链接列表（管理员） */
export const getQuickLinks = async (): Promise<ApiResponse<QuickLinkItem[]>> => {
  return request.get('/admin/quick-links');
};

/** 新增快捷链接（管理员） */
export const createQuickLink = async (params: Partial<QuickLinkItem>): Promise<ApiResponse<void>> => {
  return request.post('/admin/quick-links', params);
};

/** 更新快捷链接（管理员） */
export const updateQuickLink = async (id: number, params: Partial<QuickLinkItem>): Promise<ApiResponse<void>> => {
  return request.put(`/admin/quick-links/${id}`, params);
};

/** 删除快捷链接（管理员） */
export const deleteQuickLink = async (id: number): Promise<ApiResponse<void>> => {
  return request.delete(`/admin/quick-links/${id}`);
};
